import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { ChevronLeft, ChevronRight, X } from 'lucide-react'
import { cn } from '../lib/utils'

// Full-screen image viewer. Renders one or many images; with many it
// turns into a swipeable gallery (touch swipe + chevron buttons +
// dot counter). Escape / tap outside / X closes.
//
// Mounted via portal at document.body so position: fixed is anchored
// to the viewport regardless of which ancestor has a CSS transform
// (PullToRefresh, PageTransition, etc.).
//
// Usage:
//   const [lightbox, setLightbox] = useState(null)
//   ...
//   <img onClick={() => setLightbox({ images: [url], index: 0 })} />
//   <ImageLightbox state={lightbox} onClose={() => setLightbox(null)} />

const SWIPE_THRESHOLD_PX = 50

export default function ImageLightbox({ state, onClose }) {
  const [index, setIndex] = useState(state?.index || 0)
  const touchRef = useRef({ startX: 0, startY: 0, dx: 0, swiping: false })

  // Sync the index when the parent opens a new lightbox at a
  // different starting position (e.g. tapping the 3rd thumbnail).
  useEffect(() => {
    if (state) setIndex(state.index || 0)
  }, [state])

  // ESC closes + lock body scroll while open.
  useEffect(() => {
    if (!state) return undefined
    const onKey = (e) => {
      if (e.key === 'Escape') onClose()
      else if (e.key === 'ArrowLeft') setIndex((i) => Math.max(0, i - 1))
      else if (e.key === 'ArrowRight') {
        setIndex((i) => Math.min((state.images?.length || 1) - 1, i + 1))
      }
    }
    document.addEventListener('keydown', onKey)
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = prev
    }
  }, [state, onClose])

  if (!state || !Array.isArray(state.images) || state.images.length === 0) return null
  if (typeof document === 'undefined') return null

  const images = state.images
  const total = images.length
  const safeIndex = Math.min(Math.max(0, index), total - 1)
  const current = images[safeIndex]
  const goPrev = (e) => {
    e?.stopPropagation()
    setIndex((i) => (i - 1 + total) % total)
  }
  const goNext = (e) => {
    e?.stopPropagation()
    setIndex((i) => (i + 1) % total)
  }

  // Touch swipe — same gesture pattern as the inline RedditMedia
  // carousel, RTL-aware: in an RTL document a left-swipe means
  // "advance to the next image" because reading order is right-to-
  // left.
  const onTouchStart = (e) => {
    const t = e.touches[0]
    if (!t) return
    touchRef.current = { startX: t.clientX, startY: t.clientY, dx: 0, swiping: false }
  }
  const onTouchMove = (e) => {
    const t = e.touches[0]
    if (!t) return
    const dx = t.clientX - touchRef.current.startX
    const dy = t.clientY - touchRef.current.startY
    if (!touchRef.current.swiping && Math.abs(dx) > 8 && Math.abs(dx) > Math.abs(dy)) {
      touchRef.current.swiping = true
    }
    touchRef.current.dx = dx
  }
  const onTouchEnd = () => {
    const { dx, swiping } = touchRef.current
    if (!swiping) return
    if (Math.abs(dx) < SWIPE_THRESHOLD_PX) return
    const isRTL = typeof document !== 'undefined' && document.documentElement.dir === 'rtl'
    const goingNext = isRTL ? dx > 0 : dx < 0
    setIndex((i) => (goingNext ? (i + 1) % total : (i - 1 + total) % total))
  }

  return createPortal(
    <div
      className="fixed inset-0 z-50 bg-black/95 flex flex-col select-none"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      style={{
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)',
        overscrollBehavior: 'contain',
      }}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      {/* Header: counter + close. Counter only renders when there's
          more than one image. */}
      <div className="flex items-center justify-between p-3 flex-shrink-0">
        <span
          className="text-white/80 text-xs font-mono"
          onClick={(e) => e.stopPropagation()}
          dir="ltr"
        >
          {total > 1 ? `${safeIndex + 1} / ${total}` : ''}
        </span>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="w-9 h-9 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Image area */}
      <div
        className="flex-1 min-h-0 flex items-center justify-center px-2 relative"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={(current || '').replace(/&amp;/g, '&')}
          alt=""
          referrerPolicy="no-referrer"
          decoding="async"
          draggable={false}
          onContextMenu={(e) => e.preventDefault()}
          className="max-w-full max-h-full object-contain select-none [-webkit-touch-callout:none] [-webkit-user-drag:none]"
        />
        {total > 1 && (
          <>
            <button
              type="button"
              onClick={goPrev}
              aria-label="Previous"
              className="absolute top-1/2 -translate-y-1/2 start-2 w-10 h-10 rounded-full bg-black/60 text-white inline-flex items-center justify-center backdrop-blur-sm hover:bg-black/80"
            >
              <ChevronLeft className="w-5 h-5 rtl:-scale-x-100" />
            </button>
            <button
              type="button"
              onClick={goNext}
              aria-label="Next"
              className="absolute top-1/2 -translate-y-1/2 end-2 w-10 h-10 rounded-full bg-black/60 text-white inline-flex items-center justify-center backdrop-blur-sm hover:bg-black/80"
            >
              <ChevronRight className="w-5 h-5 rtl:-scale-x-100" />
            </button>
          </>
        )}
      </div>

      {/* Dot row footer for multi-image. Tap dot to jump. */}
      {total > 1 && total <= 12 && (
        <div
          className="flex justify-center gap-1.5 py-3 flex-shrink-0"
          onClick={(e) => e.stopPropagation()}
        >
          {images.map((_u, i) => (
            <button
              key={i}
              type="button"
              onClick={() => setIndex(i)}
              aria-label={`Image ${i + 1}`}
              className={cn(
                'w-1.5 h-1.5 rounded-full transition-colors',
                i === safeIndex ? 'bg-white' : 'bg-white/40 hover:bg-white/70',
              )}
            />
          ))}
        </div>
      )}
    </div>,
    document.body,
  )
}
